import {
	DYNAMIC_UI_CREATE_KEY,
	IMAGE_CREATE_KEY,
	isImageUpdatePrepareData,
	isImageUpdateActionData,
	isRemixGeneratedAction,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import type { ExperienceTrackerAPI } from '@atlassian/conversation-assistant-instrumentation/main';
import {
	isStreamActionComplete,
	isStreamActionPart,
	isStreamActionPrepare,
	type StreamMessage,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';

export const createChatRemixEditTracking = ({
	experienceTracker,
	conversationId,
	instrumentationId,
	sendMessageExperienceName,
	interactionSource,
	signal,
	startTime = performance.now(),
}: {
	experienceTracker: ExperienceTrackerAPI;
	conversationId: string;
	instrumentationId?: string;
	sendMessageExperienceName: string;
	interactionSource?: string;
	signal: AbortSignal;
	startTime?: number;
}): {
	fail: (error: Error) => void;
	process: (message: StreamMessage) => string | undefined;
} => {
	const enabled = interactionSource === 'chatInput';
	let started = false;
	let trackedInvocationId: string | undefined;
	const callbackIds = new Map<string, string>();
	let active = false;
	let receivedComplete = false;
	let unsubscribe: (() => void) | undefined;
	const name = ExperienceName.REMIX_REGENERATE;
	const cleanup = () => {
		active = false;
		unsubscribe?.();
		signal.removeEventListener('abort', abort);
	};
	const abort = () => {
		if (active) {
			experienceTracker.abort({ name, reason: 'User cancelled chat remix edit' });
		}
	};
	const fail = (error: Error) => {
		if (active) {
			experienceTracker.fail({ name, error });
		}
	};
	const start = (
		invocationId: string,
		attributes: {
			contentMauiId?: string;
			mediaFileId?: string;
			remixType?: string;
		},
	) => {
		if (!instrumentationId || started || signal.aborted) {
			return;
		}
		started = true;
		trackedInvocationId = invocationId;
		active = true;
		signal.addEventListener('abort', abort, { once: true });
		experienceTracker.start({
			name,
			id: instrumentationId,
			startTime,
			attributes: {
				...attributes,
				isRemixRegenerate: true,
				editSource: 'chat',
				featureArea: 'remix',
				conversationChannelId: conversationId,
				instrumentationId,
			},
			onAbort: cleanup,
			onFailure: cleanup,
			onSuccess: cleanup,
		});
		// Late-recognised edits missed the send-message start required by collect
		unsubscribe = experienceTracker.subscribe((event) => {
			if (event.name !== sendMessageExperienceName || event.id !== conversationId) {
				return;
			}
			if (event.action === 'taskFail') {
				fail(event.error);
			} else if (event.action === 'taskAbort') {
				if (signal.aborted) {
					abort();
				} else {
					fail(new Error(event.reason || 'send_message_aborted'));
				}
			} else if (event.action === 'taskSuccess') {
				unsubscribe?.();
				if (active && !receivedComplete) {
					experienceTracker.abort({ name, reason: 'stream_completed_without_action_complete' });
				}
			}
		});
	};
	return {
		fail,
		process(streamMessage: StreamMessage): string | undefined {
			if (
				!enabled ||
				!(
					isStreamActionPrepare(streamMessage) ||
					isStreamActionPart(streamMessage) ||
					isStreamActionComplete(streamMessage)
				)
			) {
				return undefined;
			}
			const { key, data, invocationId } = streamMessage.message;
			const contentType =
				typeof data === 'object' && data !== null && 'contentType' in data
					? data.contentType
					: undefined;
			let callbackId = callbackIds.get(invocationId);
			if (!callbackId) {
				if (
					key === DYNAMIC_UI_CREATE_KEY &&
					typeof data === 'object' &&
					data !== null &&
					'previousMauiId' in data &&
					typeof data.previousMauiId === 'string' &&
					data.previousMauiId &&
					(typeof contentType === 'string' ||
						(isStreamActionPrepare(streamMessage) &&
							contentType === undefined &&
							isRemixGeneratedAction(data))) &&
					contentType !== 'diagram' &&
					contentType !== 'whiteboard'
				) {
					start(invocationId, { contentMauiId: data.previousMauiId, remixType: contentType });
				} else if (
					key === IMAGE_CREATE_KEY &&
					(isImageUpdateActionData(data) ||
						(isStreamActionPrepare(streamMessage) && isImageUpdatePrepareData(data))) &&
					data.previousMediaFileId
				) {
					start(invocationId, { mediaFileId: data.previousMediaFileId, remixType: 'infographic' });
				} else {
					return undefined;
				}
				if (!started || !instrumentationId) {
					return undefined;
				}
				// Untracked callbacks must not settle a later inline edit
				callbackId =
					invocationId === trackedInvocationId
						? instrumentationId
						: `${instrumentationId}:${invocationId}`;
				callbackIds.set(invocationId, callbackId);
			}
			if (invocationId !== trackedInvocationId) {
				return callbackId;
			}
			receivedComplete ||= isStreamActionComplete(streamMessage);
			if (active && typeof data === 'object' && data !== null) {
				experienceTracker.updateAttributes({
					name,
					attributes: {
						...(key === DYNAMIC_UI_CREATE_KEY &&
							typeof contentType === 'string' && { remixType: contentType }),
						...('dynamicUiSubtype' in data && { remixSubtype: data.dynamicUiSubtype }),
					},
				});
			}
			return callbackId;
		},
	};
};
